import React, { useMemo, useState } from 'react';
import { Alert, App, Button, Card, Popconfirm, Select, Space, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import { useEmployeeOptions } from './shared/useEmployeeOptions';

const { Text } = Typography;

const AdjustmentsPage: React.FC = () => {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [period, setPeriod] = useState(dayjs());
  const { empLabel } = useEmployeeOptions();

  const params = { period_year: period.year(), period_month: period.month() + 1 };
  const { data: rows = [], isLoading } = useQuery({
    queryKey: hrmsKeys.adjustments(params),
    queryFn: () => hrmsService.listAdjustments(params),
  });

  const remove = async (id: string) => {
    try {
      await hrmsService.deleteAdjustment(id);
      message.success('Deleted');
      queryClient.invalidateQueries({ queryKey: hrmsKeys.adjustments(params) });
    } catch {
      message.error('Delete failed');
    }
  };

  const columns: ColumnsType<any> = [
    { title: 'Employee', dataIndex: 'employee_id', ellipsis: true, render: (id: string) => empLabel(id) },
    { title: 'Code', dataIndex: 'component_code', width: 100 },
    { title: 'Name', dataIndex: 'component_name' },
    {
      title: 'Salary head',
      dataIndex: 'salary_component_id',
      width: 110,
      render: (id: string | null) => (id ? <Tag color="blue">Linked</Tag> : <Tag>Manual</Tag>),
    },
    { title: 'Type', dataIndex: 'adjustment_type', width: 110 },
    { title: 'Amount', dataIndex: 'amount', width: 110 },
    {
      title: 'Recurring',
      dataIndex: 'is_recurring',
      width: 90,
      render: (v: boolean) => (v ? 'Yes' : 'No'),
    },
    { title: 'Status', dataIndex: 'status', width: 100 },
    {
      title: 'Actions',
      key: 'a',
      fixed: 'right',
      width: 80,
      render: (_, r) => (
        <Popconfirm title="Delete adjustment?" onConfirm={() => remove(r.id)}>
          <Button type="text" danger size="small" icon={<DeleteOutlined />} onClick={(e) => e.stopPropagation()} />
        </Popconfirm>
      ),
    },
  ];

  const periodOptions = useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) => {
        const d = dayjs().subtract(i, 'month');
        return { value: `${d.year()}-${d.month() + 1}`, label: d.format('MMM YYYY'), day: d };
      }),
    []
  );

  return (
    <WorkspaceLayout
      fullBleed
      header={
        <EnterprisePageHeader
          documentLabel="Additions / Deductions"
          subtitle="One-time or recurring payroll adjustments applied when you compute a period"
          primaryActions={
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() =>
                navigate(`/hr/adjustments/new?year=${params.period_year}&month=${params.period_month}`)
              }
            >
              Add adjustment
            </Button>
          }
        />
      }
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="When adjustments apply"
        description={
          <span>
            Select the payroll month first. Each line must link to a <Link to="/hr/salary-heads">Salary head</Link>{' '}
            (component code/name + GL). Recurring items continue until deleted; one-time items apply only in the chosen
            period. Compute payroll after adjustments are in place.
          </span>
        }
      />
      <Card size="small" style={{ marginBottom: 12 }}>
        <Space>
          <Select
            style={{ width: 160 }}
            value={`${period.year()}-${period.month() + 1}`}
            onChange={(v) => {
              const opt = periodOptions.find((o) => o.value === v);
              if (opt) setPeriod(opt.day);
            }}
            options={periodOptions.map(({ value, label }) => ({ value, label }))}
          />
          <Text type="secondary">{rows.length} record(s)</Text>
        </Space>
      </Card>
      <Table rowKey="id" loading={isLoading} columns={columns} dataSource={rows} pagination={{ pageSize: 20 }} />
    </WorkspaceLayout>
  );
};

export default AdjustmentsPage;
